// src/app/AppRouter.tsx
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom'

import { AdminPage } from '@/pages/Admin/AdminPage'
import { CompartmentsPage } from '@/pages/Admin/CompartmentsPage'
import { ConstraintsPage } from '@/pages/Admin/ConstraintPage/ConstraintPage'
import { DepotsPage } from '@/pages/Admin/DepotsPage'
import { DriversPage } from '@/pages/Admin/DriversPage'
import { GroupsPage } from '@/pages/Admin/GroupsPage'
import { RunsPage } from '@/pages/Admin/RunsPage/RunsPage'
import { WebAdminsPage } from '@/pages/Admin/WebAdminsPage'
import { ImportWizardPage } from '@/pages/ImportWizard/ImportWizardPage'
import { Login } from '@/pages/Login/Login'
import { OnboardingPage } from '@/pages/Onboarding/OnboardingPage'
import { VerifyEmailPage } from '@/pages/Onboarding/VerifyEmailPage'
import { OperationsPage } from '@/pages/Operations/OperationsPage'
import { PlanningPage } from '@/pages/Operations/PlanningPage'
import { RunsheetProtectedRoute } from '@/pages/Operations/routes/RunsheetProtectedRoute'
import { RunsheetPage } from '@/pages/Operations/RunsheetPage'
import { PasswordResetRequestPage } from '@/pages/PasswordResetRequestPage'
import { Registration } from '@/pages/Registration/Registration'
import { ResetPasswordPage } from '@/pages/ResetPasswordPage'
import { ManageSubscriptionPage } from '@/pages/Subscription/ManageSubscriptionPage'
import { NewSubscriptionPage } from '@/pages/Subscription/NewSubscriptionPage'
import { SubscriptionPage } from '@/pages/Subscription/SubscriptionPage'

import { Layout } from './layouts/Layout'
import { LoginRoute } from './layouts/route/LoginRoute'
import { OnboardingRoute } from './layouts/route/OnboardingRoute'

const LayoutWrapper: React.FC = () => {
  return (
    <Layout>
      <Outlet />
    </Layout>
  )
}

const OnboardingLayoutWrapper: React.FC = () => {
  return (
    <OnboardingRoute>
      <Outlet />
    </OnboardingRoute>
  )
}

export const AppRouter = () =>
  createBrowserRouter([
    {
      errorElement: <div>error</div>,
      children: [
        {
          path: '/',
          element: (
            <LoginRoute>
              <Login />
            </LoginRoute>
          ),
        },
        {
          path: '/signup',
          element: (
            <LoginRoute>
              <Registration />
            </LoginRoute>
          ),
        },
      ],
    },
    {
      path: '/account/request-password-reset',
      element: <PasswordResetRequestPage />,
    },
    {
      path: '/account/reset-password',
      element: <ResetPasswordPage />,
    },
    {
      element: <OnboardingLayoutWrapper />,
      errorElement: <div>error</div>,
      children: [
        {
          path: '/onboarding',
          element: <OnboardingPage />,
        },
        {
          path: '/verify-email',
          element: <VerifyEmailPage />,
        },
      ],
    },
    {
      path: '/upgrade',
      element: <NewSubscriptionPage />,
    },
    {
      element: <LayoutWrapper />,
      errorElement: <div>error</div>,
      children: [
        {
          path: '/subscription',
          element: <SubscriptionPage />,
          children: [
            {
              index: true,
              element: <Navigate to="manage" replace />,
            },
            {
              path: 'manage',
              element: <ManageSubscriptionPage />,
            },
          ],
        },
        {
          path: '/operations',
          element: <OperationsPage />,
          children: [
            {
              index: true,
              element: <Navigate to="planning" replace />,
            },
            {
              path: 'planning',
              element: <PlanningPage />,
            },
            {
              path: 'runsheet',
              element: (
                <RunsheetProtectedRoute>
                  <RunsheetPage />
                </RunsheetProtectedRoute>
              ),
            },
          ],
        },
        {
          path: '/import',
          element: <ImportWizardPage />,
        },
        {
          path: '/admin',
          element: <AdminPage />,
          children: [
            {
              index: true,
              element: <Navigate to="groups" replace />,
            },
            {
              path: 'groups',
              element: <GroupsPage />,
            },
            {
              path: 'depots',
              element: <DepotsPage />,
            },
            {
              path: 'webadmins',
              element: <WebAdminsPage />,
            },
            {
              path: 'runs',
              element: <RunsPage />,
            },
            {
              path: 'drivers',
              element: <DriversPage />,
            },
            {
              path: 'constraints',
              element: <ConstraintsPage />,
            },
            {
              path: 'compartments',
              element: <CompartmentsPage />,
            },
          ],
        },
      ],
    },
  ])